import React, { useState } from 'react'
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Target,
  Award,
  Calendar,
  Zap,
  Trophy,
  Share2
} from 'lucide-react'
import StrategyLevelBadge from './StrategyLevelBadge'
import StrategyProgressShareModal from './StrategyProgressShareModal'
import strategyLevelService from '../services/strategyLevelService'
import './StrategyLevelCard.css'

/**
 * Card displaying strategy level information and statistics
 */
const StrategyLevelCard = ({
  strategyLevel,
  onClick,
  onStart,
  showDetailedStats = false,
  compact = false,
  currentChallenge = null,
  publicName = null
}) => {
  const [shareModalOpen, setShareModalOpen] = useState(false)

  if (!strategyLevel) {
    return null
  }

  const {
    strategyType,
    currentLevel,
    highestLevel,
    startingLevel,
    averageScore,
    bestScore,
    totalChallenges,
    completedChallenges,
    lastChallengeAt
  } = strategyLevel

  // Check if this card's strategy matches the active challenge
  const hasActiveChallenge = currentChallenge?.strategyType === strategyType

  const displayName = strategyLevelService.getStrategyDisplayName(strategyType)
  const icon = strategyLevelService.getStrategyIcon(strategyType)

  const completionRate =
    totalChallenges > 0 ? Math.round((completedChallenges / totalChallenges) * 100) : 0

  const progressToMastery = currentLevel ? Math.round((currentLevel / 10) * 100) : 0

  // Calculate level progression
  const levelsGained = currentLevel - startingLevel
  const hasImproved = levelsGained > 0
  const isAtPeak = currentLevel === highestLevel
  const hasDropped = currentLevel < highestLevel

  // Calculate score insights
  const scoreDifference = bestScore - averageScore
  const hasHighPotential = scoreDifference > 20 // Best score is 20+ points higher than average
  const isConsistent = scoreDifference < 10 // Scores are very consistent

  // Performance trend
  const getTrendIcon = () => {
    if (hasImproved) return <TrendingUp className="trend-icon up" />
    if (hasDropped) return <TrendingDown className="trend-icon down" />
    return <Minus className="trend-icon neutral" />
  }

  const formatDate = (date) => {
    if (!date) return 'Never'
    const d = new Date(date)
    const now = new Date()
    const diff = now - d
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`
    return d.toLocaleDateString()
  }

  if (compact) {
    return (
      <>
        <div className="strategy-level-card compact">
          <div className="card-header-compact">
            <span className="strategy-icon">{icon}</span>
            <div className="strategy-info">
              <div className="strategy-title-row">
                <h4>{displayName}</h4>
                {levelsGained > 0 && <span className="level-gain-badge">+{levelsGained}</span>}
              </div>
              <div className="badge-row">
                <StrategyLevelBadge level={currentLevel} size="small" showLabel={false} />
                {hasHighPotential && (
                  <span className="insight-badge potential">
                    <Zap className="badge-icon-small" />
                    High Potential
                  </span>
                )}
                {isConsistent && completedChallenges >= 3 && (
                  <span className="insight-badge consistent">
                    <Award className="badge-icon-small" />
                    Consistent
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="card-stats-compact">
            <div className="stat-item">
              <div className="stat-with-trend">
                <span className="stat-value">{averageScore?.toFixed(0) || 0}%</span>
                {getTrendIcon()}
              </div>
              <span className="stat-label">Avg Score</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{completedChallenges || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
            {bestScore > averageScore && (
              <div className="stat-item best-score">
                <span className="stat-value best">{bestScore?.toFixed(0) || 0}%</span>
                <span className="stat-label">Best</span>
              </div>
            )}
          </div>
          {formatDate(lastChallengeAt) !== 'Never' && (
            <div className="last-challenge-time">
              <Calendar className="time-icon" />
              <span>{formatDate(lastChallengeAt)}</span>
            </div>
          )}
          <div className="card-action card-action-compact">
            {onStart && (
              <button
                className={`start-challenge-btn-compact ${hasActiveChallenge ? 'continue' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onStart()
                }}
              >
                {hasActiveChallenge ? 'Continue' : 'New'}
              </button>
            )}
            {onClick && (
              <button
                className="view-details-btn-compact"
                onClick={(e) => {
                  e.stopPropagation()
                  onClick()
                }}
              >
                Progress
              </button>
            )}
            <button
              className="share-btn-compact"
              onClick={(e) => {
                e.stopPropagation()
                setShareModalOpen(true)
              }}
              title="Share your progress"
            >
              <Share2 className="share-icon" />
            </button>
          </div>
        </div>

        <StrategyProgressShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          strategyLevel={strategyLevel}
          publicName={publicName}
        />
      </>
    )
  }

  return (
    <>
      <div className="strategy-level-card">
        <div className="card-header">
          <div className="strategy-title">
            <span className="strategy-icon-large">{icon}</span>
            <div>
              <div className="title-with-badges">
                <h3>{displayName}</h3>
                {levelsGained > 0 && (
                  <span className="level-gain-badge large">+{levelsGained} levels</span>
                )}
                {isAtPeak && highestLevel > startingLevel && (
                  <span className="peak-badge">
                    <Trophy className="badge-icon-small" />
                    Peak
                  </span>
                )}
              </div>
              <p className="strategy-subtitle">
                {completedChallenges || 0} challenge{completedChallenges !== 1 ? 's' : ''} completed
                {levelsGained > 0 &&
                  ` • Gained ${levelsGained} level${levelsGained !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <StrategyLevelBadge level={currentLevel} size="medium" />
        </div>

        {/* Level Progression Indicator */}
        {(levelsGained > 0 || hasDropped) && (
          <div className="level-progression">
            <div className="progression-line">
              <span className="level-marker start">L{startingLevel}</span>
              <div className="progression-bar">
                <div
                  className={`progression-fill ${hasImproved ? 'improved' : 'declined'}`}
                  style={{
                    width: `${((currentLevel - startingLevel) / (10 - startingLevel)) * 100}%`
                  }}
                />
              </div>
              <span className="level-marker current">L{currentLevel}</span>
              {highestLevel > currentLevel && (
                <span className="level-marker highest">L{highestLevel}</span>
              )}
            </div>
          </div>
        )}

        <div className="progress-section">
          <div className="progress-header">
            <span className="progress-label">Progress to Mastery</span>
            <span className="progress-percentage">{progressToMastery}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressToMastery}%` }} />
          </div>
        </div>

        {/* Performance Insights */}
        {(hasHighPotential || isConsistent) && (
          <div className="insights-section">
            {hasHighPotential && (
              <div className="insight-item potential">
                <Zap className="insight-icon" />
                <span>
                  High potential - your best is {scoreDifference.toFixed(0)}% above average!
                </span>
              </div>
            )}
            {isConsistent && completedChallenges >= 3 && (
              <div className="insight-item consistent">
                <Award className="insight-icon" />
                <span>Consistent performance across {completedChallenges} challenges</span>
              </div>
            )}
          </div>
        )}

        <div className="card-stats">
          <div className="stat-row">
            <div className="stat-item">
              <TrendingUp className="stat-icon" />
              <div>
                <span className="stat-value">{averageScore?.toFixed(1) || 0}%</span>
                <span className="stat-label">Average Score</span>
              </div>
            </div>
            <div className="stat-item">
              <Target className="stat-icon" />
              <div>
                <span className="stat-value">{bestScore?.toFixed(0) || 0}%</span>
                <span className="stat-label">Best Score</span>
              </div>
            </div>
          </div>

          {showDetailedStats && (
            <div className="stat-row">
              <div className="stat-item">
                <Award className="stat-icon" />
                <div>
                  <span className="stat-value">Level {highestLevel || currentLevel}</span>
                  <span className="stat-label">Highest Level</span>
                </div>
              </div>
              <div className="stat-item">
                <Calendar className="stat-icon" />
                <div>
                  <span className="stat-value">{formatDate(lastChallengeAt)}</span>
                  <span className="stat-label">Last Challenge</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card-action">
          {onStart && (
            <button
              className={`start-challenge-btn ${hasActiveChallenge ? 'continue' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                onStart()
              }}
            >
              {hasActiveChallenge ? 'Continue Challenge' : 'New Challenge'}
            </button>
          )}
          {onClick && (
            <button
              className="view-details-btn"
              onClick={(e) => {
                e.stopPropagation()
                onClick()
              }}
            >
              View Progress
            </button>
          )}
          <button
            className="share-btn"
            onClick={(e) => {
              e.stopPropagation()
              setShareModalOpen(true)
            }}
            title="Share your progress"
          >
            <Share2 className="share-icon" />
          </button>
        </div>
      </div>

      <StrategyProgressShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        strategyLevel={strategyLevel}
        publicName={publicName}
      />
    </>
  )
}

/**
 * Card for strategies not yet started by the user
 */
export const StrategyLevelCardEmpty = ({ strategyType, onStart, currentChallenge = null }) => {
  const displayName = strategyLevelService.getStrategyDisplayName(strategyType)
  const icon = strategyLevelService.getStrategyIcon(strategyType)

  // Check if this card's strategy matches the active challenge
  const hasActiveChallenge = currentChallenge?.strategyType === strategyType

  return (
    <div className="strategy-level-card empty">
      <div className="card-header">
        <div className="strategy-title">
          <span className="strategy-icon-large grayscale">{icon}</span>
          <div>
            <h3>{displayName}</h3>
            <p className="strategy-subtitle">
              {hasActiveChallenge ? 'Challenge in progress' : 'Not started yet'}
            </p>
          </div>
        </div>
      </div>

      <div className="empty-state">
        <p>
          {hasActiveChallenge
            ? 'You have an active challenge. Continue where you left off!'
            : 'Start your first challenge to begin tracking your progress!'}
        </p>
        {onStart && (
          <button className={`start-btn ${hasActiveChallenge ? 'continue' : ''}`} onClick={onStart}>
            {hasActiveChallenge ? 'Continue Challenge' : 'Start First Challenge'}
          </button>
        )}
      </div>
    </div>
  )
}

export default StrategyLevelCard
